import {StyleParamType, wixColorParam, wixFontParam, createStylesParams} from '@wix/tpa-settings'
import {StyleType} from '@wix/editor-platform-ooi-metadata-resolver'

import type {GlobalButtonStyleParams} from '../../../../../../utils/useGlobalButtonStyle'
import {colorMetadata, fontMetadata, numberMetadata} from '../../../../../../utils/builderMetadata'

export type CommonButtonStylesParams = {
  buttonDefaultCornerRadiusTopLeft: StyleParamType.Number
  buttonDefaultCornerRadiusTopRight: StyleParamType.Number
  buttonDefaultCornerRadiusBottomLeft: StyleParamType.Number
  buttonDefaultCornerRadiusBottomRight: StyleParamType.Number
}

export type HeaderButtonStylesParams = {
  headerButtonDefaultFont: StyleParamType.Font
  headerButtonDefaultColor: StyleParamType.Color
  headerButtonDefaultFillColor: StyleParamType.Color
  headerButtonDefaultBorderWidth: StyleParamType.Number
  headerButtonDefaultBorderColor: StyleParamType.Color

  headerButtonHoverFont: StyleParamType.Font
  headerButtonHoverColor: StyleParamType.Color
  headerButtonHoverFillColor: StyleParamType.Color
  headerButtonHoverBorderColor: StyleParamType.Color
}

export type FormButtonStylesParams = {
  formButtonDefaultFont: StyleParamType.Font
  formButtonDefaultColor: StyleParamType.Color
  formButtonDefaultFillColor: StyleParamType.Color
  formButtonDefaultBorderWidth: StyleParamType.Number
  formButtonDefaultBorderColor: StyleParamType.Color

  formButtonHoverFont: StyleParamType.Font
  formButtonHoverColor: StyleParamType.Color
  formButtonHoverFillColor: StyleParamType.Color
  formButtonHoverBorderColor: StyleParamType.Color
}

export const commonButtonStylesParams = createStylesParams<CommonButtonStylesParams>({
  buttonDefaultCornerRadiusTopLeft: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: numberMetadata(StyleType.borderTopLeftRadius, {defaultValue: 0}),
  },
  buttonDefaultCornerRadiusTopRight: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: numberMetadata(StyleType.borderTopRightRadius, {defaultValue: 0}),
  },
  buttonDefaultCornerRadiusBottomLeft: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: numberMetadata(StyleType.borderBottomLeftRadius, {defaultValue: 0}),
  },
  buttonDefaultCornerRadiusBottomRight: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: numberMetadata(StyleType.borderBottomRightRadius, {defaultValue: 0}),
  },
})

export const headerButtonStylesParams = createStylesParams<HeaderButtonStylesParams>({
  headerButtonDefaultFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {size: 16}),
    metadata: fontMetadata('Body-M', 16),
  },
  headerButtonDefaultColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: colorMetadata(StyleType.color, 'color-1'),
  },
  headerButtonDefaultFillColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8', 1),
    metadata: colorMetadata(StyleType.backgroundColor, 'color-8', 1),
  },
  headerButtonDefaultBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: numberMetadata(StyleType.borderWidth, {defaultValue: 0}),
  },
  headerButtonDefaultBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-line', 0.12),
    metadata: colorMetadata(StyleType.borderColor, 'color-line', 0.12),
  },

  headerButtonHoverFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 16,
      style: {bold: false, italic: false, underline: true},
    }),

    metadata: fontMetadata('Body-M', 16),
  },
  headerButtonHoverColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: colorMetadata(StyleType.color, 'color-1'),
  },
  headerButtonHoverFillColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-action', 1),
    metadata: colorMetadata(StyleType.backgroundColor, 'color-action', 1),
  },
  headerButtonHoverBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-line', 0.12),
    metadata: colorMetadata(StyleType.borderColor, 'color-line', 0.12),
  },
})

export const formButtonStylesParams = createStylesParams<FormButtonStylesParams>({
  formButtonDefaultFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {size: 16}),
    metadata: fontMetadata('Body-M', 16),
  },
  formButtonDefaultColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: colorMetadata(StyleType.color, 'color-1'),
  },
  formButtonDefaultFillColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8', 1),
    metadata: colorMetadata(StyleType.backgroundColor, 'color-8', 1),
  },
  formButtonDefaultBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: numberMetadata(StyleType.borderWidth, {defaultValue: 0}),
  },
  formButtonDefaultBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-line', 0.12),
    metadata: colorMetadata(StyleType.borderColor, 'color-line', 0.12),
  },

  formButtonHoverFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 16,
      style: {bold: false, italic: false, underline: true},
    }),

    metadata: fontMetadata('Body-M', 16),
  },
  formButtonHoverColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: colorMetadata(StyleType.color, 'color-1'),
  },
  formButtonHoverFillColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-action', 1),
    metadata: colorMetadata(StyleType.backgroundColor, 'color-action', 1),
  },
  formButtonHoverBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-line', 0.12),
    metadata: colorMetadata(StyleType.borderColor, 'color-line', 0.12),
  },
})

export const headerGlobalButtonStyleParams: GlobalButtonStyleParams = {
  textColor: headerButtonStylesParams.headerButtonDefaultColor,
  fillColor: headerButtonStylesParams.headerButtonDefaultFillColor,
  borderColor: headerButtonStylesParams.headerButtonDefaultBorderColor,
  borderWidth: headerButtonStylesParams.headerButtonDefaultBorderWidth,
  hoverTextColor: headerButtonStylesParams.headerButtonHoverColor,
  hoverFillColor: headerButtonStylesParams.headerButtonHoverFillColor,
  hoverBorderColor: headerButtonStylesParams.headerButtonHoverBorderColor,
}

export const formGlobalButtonStyleParams: GlobalButtonStyleParams = {
  textColor: formButtonStylesParams.formButtonDefaultColor,
  fillColor: formButtonStylesParams.formButtonDefaultFillColor,
  borderColor: formButtonStylesParams.formButtonDefaultBorderColor,
  borderWidth: formButtonStylesParams.formButtonDefaultBorderWidth,
  hoverTextColor: formButtonStylesParams.formButtonHoverColor,
  hoverFillColor: formButtonStylesParams.formButtonHoverFillColor,
  hoverBorderColor: formButtonStylesParams.formButtonHoverBorderColor,
}
